// Mutations
import {
  SET_USER,
  WIPE_USER,
  SET_USER_PREFERENCES,
  UPDATE_USER_CLIENT,
  SET_SELECTED_UNIVERSES,
  SET_UNIVERSES,
  TOGGLE_UNIVERSES,
  UPDATE_USER_IS_FIRST_SIGNIN,
  SET_USER_SURVEY,
  SET_PROVISIONAL_USER_PREFERENCES,
  SET_MARKET_MOVER_EMAIL_NOTIFICATIONS,
  SET_MARKET_MOVER_EMAIL_NOTIFICATIONS_DIRTY,
  SET_IN_APP_NOTIFICATION_PREFERENCES,
  SET_IN_APP_NOTIFICATION_PREFERENCES_DIRTY,
} from './mutations'

// Libs
import gQLMixin, { handleGraphQLErr } from '@/mixins/graphqlMixin.js'

// Graph QL
import * as gqlQuery from './queries.graphql'
import * as gqlMutation from './mutations.graphql'
import { instance } from '@cognitivecredit/ui-utils'
import { ActionContext } from 'vuex'
import {
  License,
  MarketMoverEmailNotifications,
  UserPreferences,
  InAppNotificationPreferences,
} from '@/stores/User/state'
import { useWatchlistsStore } from '@/piniaStores/watchlists/watchlistsStore'
import { storeToRefs } from 'pinia'

const userActions = {
  async navigate(_: ActionContext, payload) {
    try {
      await gQLMixin.methods.communicate(gqlMutation.Navigate, payload)
    } catch (err) {
      console.error('[ERROR] Logging user navigation', err)
      return err
    }
  },
  async setUser({ commit }: ActionContext, user) {
    commit(SET_USER, user)
  },
  async setUserIsFirstSignIn({ commit }: ActionContext, isFirstSignIn) {
    commit(UPDATE_USER_IS_FIRST_SIGNIN, isFirstSignIn)
  },
  async checkEnabledFeatures({ commit }: ActionContext) {
    try {
      const req = await instance({ prefix: 'user-management' })
      const clientRes = await req.get('/client')
      const clientData = clientRes.data.client
      commit(UPDATE_USER_CLIENT, {
        displayName: clientData.displayName,
        loanTradingDesk: clientData.loanTradingDesk,
        status: clientData.status,
        trialDates: clientData.trialDates,
        chatApp: clientData.chatApp,
        hasInstruments: clientData.featureInstruments,
      })
    } catch (err) {
      console.error('Error getting the feature flag permissions', err)
      return err
    }
  },
  wipeUser({ commit }: ActionContext) {
    commit(WIPE_USER)
  },
  async requestUpdateStatus(_: ActionContext, payload) {
    const query = payload.type === 'financials' ? gqlQuery.GetUpdateStatus : gqlQuery.GetForecastUpdateStatus
    try {
      const res = await gQLMixin.methods.communicate(query, { batchId: payload.batchId })
      return res.data.updateStatus
    } catch (err) {
      console.error('[ERROR] Requesting the update status of the forecast model', err)
      return err
    }
  },
  async toggleLicense({ commit, dispatch, getters }: ActionContext, payload: License) {
    commit(TOGGLE_UNIVERSES, payload)

    dispatch('saveNewPreference', {
      universes: getters['selectedUniverseIds'],
    })
  },
  async getLicenseList({ commit, state, dispatch }: ActionContext) {
    try {
      const {
        data: { licenseList },
      } = await gQLMixin.methods.communicate(gqlQuery.GetLicenseList)

      commit(SET_UNIVERSES, licenseList)
      const watchlistStore = useWatchlistsStore()
      const { anyWatchlistActive } = storeToRefs(watchlistStore)
      if (!anyWatchlistActive.value && !state.selectedUniverses?.length && state.preferences?.selectedUniverses) {
        dispatch('getSelectedUniverses')
      }
    } catch (err) {
      handleGraphQLErr(err)
      return err
    }
  },
  async getSelectedUniverses({ commit, state, getters }) {
    if (!state.licenseList?.length || !getters['preferences'].selectedUniverses?.length) {
      return
    }

    const universes = getters['preferences'].selectedUniverses
    const initialUniverses = state.licenseList?.filter((universe) => universes.includes(universe.id) && universe)
    commit(SET_SELECTED_UNIVERSES, initialUniverses)
  },
  /**
   * @param { Array } payload - the list of universe
   * @param { Object } payload[0] - { id: String, name: String, availableToMe: Boolean }
   */
  setSelectedUniverses({ commit, dispatch }, payload) {
    commit(SET_SELECTED_UNIVERSES, payload)
    // moving this from the CompanyFilter component so we don't
    // trigger two different fetches of the company list
    // when changing universes
    dispatch('Company/setSearchParam')
    dispatch('Company/setCompanySorting', { name: 'ASC', reset: true })
  },
  async saveNewPreference({ commit }: ActionContext, payload) {
    try {
      const req = await instance({ prefix: 'user-management' })
      const { data } = await req({ method: 'patch', url: '/me/preferences', data: payload })

      if (!data) {
        // eslint-disable-next-line prettier/prettier
        throw new Error('[ERROR] Couldn\'t save preferences')
      }

      commit(SET_USER_PREFERENCES, data)
    } catch (err) {
      console.error('[ERROR] Setting the user preference', err)
      return err
    }
  },
  async getUserPreferences({ commit, state, dispatch }) {
    try {
      const req = await instance({ prefix: 'user-management' })
      const res = await req.get('/user/preferences')
      const watchlistStore = useWatchlistsStore()
      const { anyWatchlistActive } = storeToRefs(watchlistStore)
      if (!res.data) {
        throw 'The user preferences object is not being returned as expected'
      }
      commit(SET_USER_PREFERENCES, res.data)
      if (!anyWatchlistActive.value && !state.selectedUniverses?.length) {
        dispatch('getSelectedUniverses')
      }
      return res.data
    } catch (err) {
      console.error('[ERROR] Getting the user preferences', err)
      throw err
    }
  },
  async setSurvey({ commit }: ActionContext) {
    try {
      const req = await instance({ prefix: 'user-management' })
      const res = await req.get('/nps/survey')
      if (res.data) {
        commit(SET_USER_SURVEY, res.data)
        return res.data
      }
    } catch (err) {
      console.error('[ERROR] Getting the user survey', err)
    }
  },
  async setNewPreference({ commit }: ActionContext, payload: Partial<UserPreferences>) {
    commit(SET_USER_PREFERENCES, payload)
  },
  async setProvisionalPreference({ commit }: ActionContext, payload: Partial<UserPreferences>) {
    commit(SET_PROVISIONAL_USER_PREFERENCES, payload)
  },
  async getMarketMoverEmailNotifications({ commit }: ActionContext) {
    try {
      const watchlistStore = useWatchlistsStore()
      if (!watchlistStore.watchlists.length) {
        await watchlistStore.loadWatchlists()
      }

      const req = await instance({ prefix: 'user-management' })
      const { data }: { data: MarketMoverEmailNotifications } = await req({
        method: 'get',
        url: '/user/market-movers-email-notifications',
      })

      const validWatchlistIds = new Set(watchlistStore.watchlists.map((w) => w.id))

      const pickPeriod = (period: MarketMoverEmailNotifications['daily']): MarketMoverEmailNotifications['daily'] => ({
        allCompanies: period.allCompanies,
        watchlistIds: period.watchlistIds.filter((id) => validWatchlistIds.has(id)),
      })

      const filteredData: MarketMoverEmailNotifications = {
        daily: pickPeriod(data.daily),
        weekly: pickPeriod(data.weekly),
        monthly: pickPeriod(data.monthly),
      }

      commit(SET_MARKET_MOVER_EMAIL_NOTIFICATIONS, filteredData)
      commit(SET_MARKET_MOVER_EMAIL_NOTIFICATIONS_DIRTY, false)
    } catch (err) {
      console.error('[ERROR] Getting market mover email notifications', err)
      return err
    }
  },
  async saveMarketMoverEmailNotifications({ commit }: ActionContext, payload: MarketMoverEmailNotifications) {
    try {
      const req = await instance({ prefix: 'user-management' })
      const { data } = await req({
        method: 'put',
        url: '/me/market-movers-email-notifications',
        data: payload,
      })
      const updatedData = data ?? payload
      commit(SET_MARKET_MOVER_EMAIL_NOTIFICATIONS, updatedData)
      commit(SET_MARKET_MOVER_EMAIL_NOTIFICATIONS_DIRTY, false)
    } catch (err) {
      console.error('[ERROR] Saving market mover email notifications', err)
      return err
    }
  },
  async setMarketMoverEmailNotifications({ commit }: ActionContext, payload: MarketMoverEmailNotifications) {
    commit(SET_MARKET_MOVER_EMAIL_NOTIFICATIONS, payload)
    commit(SET_MARKET_MOVER_EMAIL_NOTIFICATIONS_DIRTY, true)
  },
  async getInAppNotificationPreferences({ commit }: ActionContext) {
    try {
      const watchlistStore = useWatchlistsStore()
      if (!watchlistStore.watchlists.length) {
        await watchlistStore.loadWatchlists()
      }

      const validWatchlistIds = new Set(watchlistStore.watchlists.map((w) => w.id))

      const req = await instance({ prefix: 'user-management' })
      const { data } = await req.get('me/in-app-notifications')
      // Fetch watchlist ids from the backend and filter out any that are no longer valid (e.g. deleted watchlists)
      data.modelUpdate.popup.watchlistIds = data.modelUpdate.popup.watchlistIds.filter((id) =>
        validWatchlistIds.has(id),
      )
      data.modelUpdate.panel.watchlistIds = data.modelUpdate.panel.watchlistIds.filter((id) =>
        validWatchlistIds.has(id),
      )

      commit(SET_IN_APP_NOTIFICATION_PREFERENCES, data)
      return data
    } catch (err) {
      console.error('[ERROR] Getting in-app notification preferences', err)
      throw err
    }
  },
  async setInAppNotificationPreferences({ commit }: ActionContext, payload: InAppNotificationPreferences) {
    commit(SET_IN_APP_NOTIFICATION_PREFERENCES, payload)
    commit(SET_IN_APP_NOTIFICATION_PREFERENCES_DIRTY, true)
  },
  async saveInAppNotificationPreferences({ commit }: ActionContext, payload: InAppNotificationPreferences) {
    try {
      const req = await instance({ prefix: 'user-management' })
      const { data } = await req.post('me/in-app-notifications', payload)

      const updatedData = data ?? payload
      commit(SET_IN_APP_NOTIFICATION_PREFERENCES, updatedData)
      commit(SET_IN_APP_NOTIFICATION_PREFERENCES_DIRTY, false)
    } catch (err) {
      console.error('[ERROR] Saving in-app notification preferences', err)
      throw err
    }
  },
}
export type UserActions = typeof userActions
export default userActions
